import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MailWarning } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { setPageTitle } from "../lib/format";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ status: "idle", message: null });
  const [busy, setBusy] = useState(false);
  const tried = useRef(false);
  const token = params.get("token");

  useEffect(() => {
    setPageTitle("Verify email");
    if (!token || tried.current) return;
    tried.current = true;
    setState({ status: "verifying", message: null });
    api.verifyEmail(token)
      .then(async (r) => { setState({ status: "ok", message: r.message }); try { await refresh(); } catch { /* optional */ } })
      .catch((e) => setState({ status: "error", message: e.message }));
  }, [token, refresh]);

  const resend = async () => {
    setBusy(true);
    try { const r = await api.verifyEmailRequest(); toast.success(r.message || "Verification email sent"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="verify-email-page">
      <Navbar />
      <main className="flex-1 max-w-xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <div className="card-dark p-8 text-center">
          {state.status === "verifying" && (
            <><Loader2 className="w-8 h-8 animate-spin text-[#FF3B45] mx-auto" /><p className="mt-4 text-sm text-zinc-400">Verifying your email…</p></>
          )}
          {state.status === "ok" && (
            <><CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" data-testid="verify-success-icon" />
              <h1 className="type-h2 mt-4">Email verified</h1>
              <p className="mt-2 text-sm text-zinc-400" data-testid="verify-status">{state.message}</p>
              <Link to="/dashboard" className="btn-primary inline-flex mt-6 text-sm" data-testid="verify-continue-btn">Continue</Link></>
          )}
          {state.status === "error" && (
            <><MailWarning className="w-10 h-10 text-amber-400 mx-auto" />
              <h1 className="type-h2 mt-4">Link expired or invalid</h1>
              <p className="mt-2 text-sm text-zinc-400" data-testid="verify-status">{state.message}</p>
              {user && <button onClick={resend} disabled={busy} className="btn-primary mt-6 text-sm" data-testid="resend-verification-btn">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send a new verification email</button>}
              {!user && <Link to="/login" className="btn-primary inline-flex mt-6 text-sm">Sign in to request a new link</Link>}</>
          )}
          {state.status === "idle" && !token && (
            <><h1 className="type-h2">Verify your email</h1>
              {user ? (
                <>
                  <p className="mt-3 text-sm text-zinc-400">We'll send a secure single-use link to <strong className="text-zinc-200">{user.email}</strong>.</p>
                  <button onClick={resend} disabled={busy} className="btn-primary mt-6 text-sm" data-testid="resend-verification-btn">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send verification email</button>
                </>
              ) : (
                <>
                  <p className="mt-3 text-sm text-zinc-400">Sign in to request a verification link.</p>
                  <Link to="/login" className="btn-primary inline-flex mt-6 text-sm">Sign in</Link>
                </>
              )}</>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
